/**
 * @description Reads an individual knowledge entry's settings. Its used to populate the knowledge dialog form when editing
 *  a knowledge entry.
 */

import { useQuery } from '@tanstack/react-query';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';

import decodeJson from '@seccon/shared/utils/decodeJson';
import ApiError from '@seccon/shared/utils/ApiError';

import { CATEGORIES, ROLES } from '../constants';

const ROLES_MAP = keyBy(ROLES, (entry) => entry.value);
const CATEGORY_MAP = keyBy(CATEGORIES, (entry) => entry.value);

// eslint-disable-next-line arrow-body-style
const useKnowledgeEntry = (id = null) => {
  return useQuery({
    enabled: false,
    queryKey: ['fetchKnolwedgeEntry', id],
    queryFn: async () => (
      fetch(`/knowledgeCenter/getDetails/${ id }`)
        .then(decodeJson)
        .catch(({ body, status }) => {
          throw new ApiError('Unable to fetch knowledge center entry', body, status);
        })
    ),
    // Process the response and convert it into form data values
    select: (resp) => ({
      // Category must be a label/value pair based on the category map (default to OTHER if the category cannot be found in the map)
      category: pick(CATEGORY_MAP[resp.category] ?? CATEGORY_MAP.Other, ['label', 'value']),
      // Convert the embedSrc url to an iframe tag
      embedSrc: resp.embedSrc ? `<iframe src="${ resp.embedSrc }" />` : '',
      // Convert the list of role values to a list of label/value pairs (but only if the value can be found in the roles map)
      roles: resp.roleList?.reduce((list, entry) => {
        if (ROLES_MAP[entry]) {
          list.push(ROLES_MAP[entry]);
        }

        return list;
      }, [])
    })
  });
};

export default useKnowledgeEntry;
